import { Badge, Button, ChoiceCard } from '@indipia/ui-core'
import type { NavFn } from '../pages/SchedulerPage'
import { StepIndicator } from './SiteSelectView'
import { InfoBanner } from '../components/InfoBanner'
import type { ExtractResult } from './extractTypes'

// 생성 방법 선택(create-upload) — 견적서 올리기 / 직접 입력하기 두 갈래 선택 화면.
// 파일 업로드·AI 분석은 이 화면에서 하지 않는다. '견적서 올리기'는 업로드 화면(create-upload2)으로 연결하고,
// 그 화면에서 AI 분석(/schedule/estimate-analyses/raw)을 수행한다(F8 ① · 이동 규칙 F1_이동규칙.md).

type Props = {
  navigate: NavFn
  resetSiteId?: number
  onResult: (r: ExtractResult) => void
  /** 스튜디오 홈에서 넘어온 진입모드 — 'QUOTE'이면 견적서 업로드, 'MANUAL'이면 직접 입력 쪽을 강조 */
  entry?: string | null
}

export function UploadView({ navigate, resetSiteId, onResult, entry }: Props) {
  // entry=QUOTE → 견적서 업로드, entry=MANUAL → 직접 입력에 "추천" 배지를 붙인다.
  // (hover 시 강조는 ChoiceCard 의 .choice-card-body:hover 가 처리한다)
  const highlight = entry === 'MANUAL' ? 'direct' : entry === 'QUOTE' ? 'upload' : null
  // entry=QUOTE(스튜디오 홈 "내 견적서로 스케줄 자동으로 만들어요")로 들어온 흐름은
  // 견적서가 이미 있다는 전제라 "직접 입력하기" 카드를 보여줄 필요가 없다.
  const showDirectInput = entry !== 'QUOTE'

  // 견적서 올리기 — 업로드 화면으로 이동(생성 경로 QUOTE 확정은 SchedulerPage.navigate 가 처리)
  const handleQuote = () => {
    navigate('create-upload2')
  }

  const handleDirectInput = () => {
    onResult({ works: [], skipped: true })
    navigate('create-extract')
  }

  const handleBack = () => {
    navigate(resetSiteId ? 'site-list' : 'create-site')
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-surface px-4 py-6">
      <div className="w-full max-w-[680px]">
        <StepIndicator step={resetSiteId ? 0 : 1} resetMode={!!resetSiteId} />

        <InfoBanner>
          {entry === 'QUOTE'
            ? '견적서 파일만 올리면 AI가 공종과 일정을 자동으로 만들어줘요 — 가장 빠르고 쉬운 방법이에요'
            : '두 가지 방법 중 편한 방법으로 공사 스케줄을 만들어보세요'}
        </InfoBanner>

        <div className="mb-4 flex gap-3.5">
          {/* 견적서 올리기 — 클릭 액션(업로드 화면으로 이동). 클릭 전용 선택 카드 ChoiceCard 사용 */}
          <ChoiceCard
            highlight={highlight === 'upload'}
            badge={highlight === 'upload' ? <Badge status="accent">추천</Badge> : undefined}
            onClick={handleQuote}
            icon={
              /* 아이콘 48(D-09 §15.17). strokeWidth 는 viewBox 24 기준이라 1.5 = 3px 렌더 — 공용 아이콘과 같은 획 */
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
            }
            title="견적서 올리기"
            description={<><strong>파일만 올리면 공종과 일정을 자동으로 만들어줘요</strong><br />가장 빠르고 쉬운 방법이에요</>}
            hint="엑셀 · PDF · 이미지 견적서를 다음 화면에서 올려요"
          />

          {/* 직접 입력 — entry=QUOTE(견적서 보유 전제 흐름)에서는 아예 노출하지 않는다. */}
          {showDirectInput && (
            <ChoiceCard
              highlight={highlight === 'direct'}
              badge={highlight === 'direct' ? <Badge status="accent">추천</Badge> : undefined}
              onClick={handleDirectInput}
              icon={
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              }
              title="직접 입력하기"
              description={<><strong>공종과 기간을 원하는 대로 자유롭게 설정할 수 있어요</strong><br />세부 조정이 필요할 때 좋아요</>}
              hint="공종을 하나씩 수동으로 설정"
            />
          )}
        </div>

        <div className="mt-4 flex gap-2.5 border-t border-border pt-4">
          <Button variant="secondary" size="lg" onClick={handleBack}>
            이전
          </Button>
        </div>
      </div>
    </div>
  )
}
